import { useReactFlow, type Node } from '@xyflow/react';
import { useCallback } from 'react';

/**
 * Hook for creating various node types in the flow
 * @param addNode Function to add node to the flow
 * @param userEmail Email of the current user
 * @returns Object with functions to create different node types
 */
export function useNodeCreation(addNode: (node: Node) => void, userEmail: string) {
  const { screenToFlowPosition, getNodes, setCenter } = useReactFlow();

  const createQuickStartNode = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    }); const newNode: Node = {
      id: `quickstart-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodequickstart',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'Quick Start Node',
        informationClassification: 'Information',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        minimizeLevel: 'normal',
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createNodeQuickStartDynamic = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `quickstart-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodequickstartdynamic',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'Quick Start Dynamic Node',
        informationClassification: 'Information',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createnodequickstartdynamic = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `quickstartdynamic-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodequickstartdynamic',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'Quick Start Dynamic Node',
        informationClassification: 'Information',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createDocumentNode = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `document-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodedocument',
      position,
      width: 1200,
      height: 1600,
      data: {
        label: 'Document Node',
        informationClassification: 'Synthesis',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        analysisAndRecommendations: true,
        version: 1,
      },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createDocumentNodeWithContent = useCallback((contentMarkdown: string, label?: string, sourcePosition?: { x: number; y: number }) => {
    const position = sourcePosition ?
      {
        x: sourcePosition.x + 1200 + 50, // Position to the right of source node
        y: sourcePosition.y
      } :
      screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

    const newNode: Node = {
      id: `document-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodedocument',
      position,
      width: 1200,
      height: 1600,
      data: {
        label: label || 'Document Node',
        informationClassification: 'Synthesis',
        contentMarkdown,
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        analysisAndRecommendations: true,
        version: 1,
      },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    addNode(newNode);
    return newNode;
  }, [screenToFlowPosition, addNode, userEmail]);

  const createNodeIntellica = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `node-intellica-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodeintellica',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'Intellica',
        informationClassification: 'Wisdom',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createNodeSmartNotes = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `smartnotes-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodesmartnotes',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'Smart Notes',
        informationClassification: 'Knowledge',
        content: '',
        recommendations: [],
        statusMessage: '',
        isMinimized: false,
        isLocked: false,
        assignedEmails: [],
        documentRequest: undefined,
        linkedKnowledge: '',
        extractLinks: true,
        setReferences: {},
        context: '',
        dataSettings: {},
        configuration: {},
        linkedNodes: [],
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createDocumentDesignerNode = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `document-designer-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodedocumentdesigner',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'Document Designer',
        informationClassification: 'Synthesis',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        version: 1,
      },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createNodeDiagrams = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `diagram-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodediagrams',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'PlantUML Diagram',
        informationClassification: 'Information',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createNodeDiagramsMermaid = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `mermaid-diagram-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodediagramsmermaid',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'Mermaid Diagram',
        informationClassification: 'Information',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createFileMinerNode = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `file-miner-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodefileminer',
      position,
      width: 900,
      height: 1222,
      data: {
        label: 'File Miner',
        informationClassification: 'Knowledge',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        blobIds: [],
        userPrompt: 'Analyze these files and provide insights',
        systemPrompt: 'You are a helpful assistant analyzing files. Provide detailed analysis based on the content.',
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createJavascriptNode = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `javascript-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodejavascript',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'Javascript',
        informationClassification: 'Deliverable',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createImageGeneratorNode = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `image-generator-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodeimagegenerator',
      position,
      width: 996,
      height: 918,
      data: {
        label: 'Image Generator',
        informationClassification: 'Deliverable',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        promptText: '', // Initial empty prompt
        generatedImages: [], // Array to store generated image data
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createNodeResource = useCallback((fileData: {
    fileId: string;
    filename: string;
    url: string;
    contentType: string;
    size: number;
    createdAt: string;
    groupId: string | null;
    position?: { x: number; y: number }; // Allow optional position
  }) => {
    const position = fileData.position
      ? fileData.position
      : screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

    const newNode: Node = {
      id: `resource-node-${Date.now()}`,
      type: 'noderesource',
      position,
      width: 464,
      height: 692,
      data: {
        label: 'File Resource',
        informationClassification: 'Data',
        filename: fileData.filename,
        createdBy: userEmail,
        createdAt: fileData.createdAt,
        url: fileData.url,
        fileId: fileData.fileId,
        contentType: fileData.contentType,
        size: fileData.size,
        groupId: fileData.groupId,
        version: 1,
      },
    };

    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createStrategyNode = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `strategy-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodestrategy',
      position,
      width: 400,
      height: 500,
      data: {
        label: 'Strategy: Situational Awareness Mapping',
        informationClassification: 'Synthesis',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        currentSituationQuality: 'Moderate',
        currentSituationDetails: 'Initial assessment needed',
        targetSituationQuality: 'Not Assessed',
        targetSituationDetails: '',
        middleStepsQuality: 'Not Assessed',
        middleStepsDetails: '',
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  // Create Presence node function with fixed ID
  const createPresenceNode = useCallback(() => {
    // Check if presence node already exists
    const existingPresenceNode = getNodes().find(n => n.id === "Presence-1" || n.type === 'nodepresence');
    if (existingPresenceNode) {
      // Node already exists, select it instead of creating a new one
      console.log("Presence node already exists, selecting it");
      const nodePosition = existingPresenceNode.position;
      setCenter(nodePosition.x, nodePosition.y, { zoom: 1, duration: 400 });
      return;
    }

    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: "Presence-1", // Fixed ID for the presence node
      type: 'nodepresence',
      position,
      width: 500,
      height: 600,
      data: {
        label: 'Project Presence',
        informationClassification: 'Information',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        activeUsers: {},
        systemState: {
          lastCheck: Date.now(),
          isHealthy: true
        },
        chatMessages: [],
        version: 1,
      },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail, getNodes, setCenter]);

  const createAgencyNode = useCallback(() => {
    // Check if agency node already exists
    const existingAgencyNode = getNodes().find(n => n.id === "Agency-1" || n.type === 'nodeagency');
    if (existingAgencyNode) {
      // Node already exists, select it instead of creating a new one
      console.log("Agency node already exists, selecting it");
      const nodePosition = existingAgencyNode.position;
      setCenter(nodePosition.x, nodePosition.y, { zoom: 1, duration: 400 });
      return;
    }

    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: "Agency-1", // Fixed ID for the agency node
      type: 'nodeagency',
      position,
      width: 500,
      height: 600,
      data: {
        label: 'Project Agency',
        informationClassification: 'Wisdom',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        agents: [
          {
            id: `agent-intellica-${Date.now()}`,
            name: 'Intellica',
            description: 'Lead intelligence agent that helps with project organization and analysis',
            isActive: true,
            nodes: [],
            lastActivity: Date.now(),
            capabilities: ['analyze', 'assist', 'generate', 'recommend'],
            messages: []
          }
        ],
        intellicaMessages: [
          {
            id: `welcome-${Date.now()}`,
            content: 'Hello! I am Intellica, your lead intelligence agent. How can I assist you with your project today?',
            email: 'intellica@system.ai',
            isIntelligenceResponse: true,
            timestamp: Date.now()
          }
        ],
        systemState: {
          lastCheck: Date.now(),
          isHealthy: true
        },
        version: 1,
      },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail, getNodes, setCenter]);

  const createGlobalConstantsNode = useCallback(() => {
    // Check if GlobalConstants node already exists
    const existingGlobalConstantsNode = getNodes().find(n => n.id === "GlobalConstants-1" || n.type === 'nodeglobalconstants');
    if (existingGlobalConstantsNode) {
      // Node already exists, select it instead of creating a new one
      console.log("Global Constants node already exists, selecting it");
      const nodePosition = existingGlobalConstantsNode.position;
      setCenter(nodePosition.x, nodePosition.y, { zoom: 1, duration: 400 });
      return;
    }

    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: "GlobalConstants-1", // Fixed ID for the global constants node
      type: 'nodeglobalconstants',
      position,
      width: 800,
      height: 600,
      data: {
        label: 'Global Constants',
        informationClassification: 'Wisdom',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        manualConstants: '# Global Constants\n\n## Project Information\nproject_name = "My Project"\nversion = "1.0.0"\n\n## Common Values\nauthor = "Your Name"\ndate = "' + new Date().toLocaleDateString() + '"\n\n## Settings\ndebug_mode = false\nmax_items = 100',
        linkedConstants: '',
        combinedConstants: '',
        configuration: {
          globalconstants: true,
        },
        dataSettings: {
          exposedData: {
            manualConstants: {
              label: 'Manual Constants',
              description: 'User-defined constants and variables',
              type: 'text'
            },
            linkedConstants: {
              label: 'Linked Constants',
              description: 'Constants built from referenced nodes',
              type: 'text'
            },
            combinedConstants: {
              label: 'Combined Constants',
              description: 'All constants combined (manual + linked)',
              type: 'text'
            }
          }
        },
        setReferences: {},
        isLocked: false,
        isMinimized: false,
        version: 1,
      },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail, getNodes, setCenter]);

  const createNodeAgent = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `node-agent-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodeagent',
      position,
      width: 400,
      height: 600,
      data: {
        label: 'Agent',
        informationClassification: 'Wisdom',
        name: 'New Agent',
        role: 'general',
        type: 'assistant',
        enabled: false,
        instructions: '',
        triggerEvents: [],
        observationList: [],
        settings: {},
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        dataSettings: {
          inputLinks: [],
          outputLinks: [],
          exposedData: {
            agentId: {
              value: '',
              enabled: false,
              label: 'Agent ID',
              description: 'Unique identifier for this agent',
              permissions: { readable: true, writable: false },
            },
            name: {
              value: 'New Agent',
              enabled: true,
              label: 'Agent Name',
              description: 'Display name of the agent',
              permissions: { readable: true, writable: false },
            },
            role: {
              value: 'general',
              enabled: true,
              label: 'Agent Role',
              description: 'Role/profession of the agent',
              permissions: { readable: true, writable: false },
            },
            enabled: {
              value: false,
              enabled: true,
              label: 'Agent Enabled',
              description: 'Whether the agent is currently active',
              permissions: { readable: true, writable: false },
            },
            observationList: {
              value: [],
              enabled: true,
              label: 'Observed Nodes',
              description: 'List of node IDs this agent is observing',
              permissions: { readable: true, writable: false },
            },
          },
          availableNodes: [],
          autoSync: false,
        },
        version: 1,
      },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createnodeknowledgeset = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `knowledge-set-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodeknowledgeset',
      position,
      width: 1000,
      height: 800,
      data: {
        label: 'Knowledge Set',
        informationClassification: 'Knowledge',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        contentMarkdown: '',
        linkedKnowledge: '',
        version: 1,
      },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createNodeComposite = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `composite-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodecomposite',
      position,
      width: 1200,
      height: 800,
      data: {
        label: 'Composite Document',
        informationClassification: 'Deliverable',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        contentMarkdown: '',
        linkedKnowledge: '',
        configuration: {
          markdownFormatVisible: true,
          showPublishedFiles: true,
          enableDragAndDrop: true,
          autoRefreshSections: false,
          showComments: false,
          showDebugInfo: false,
          showSectionState: true,
          showSections: true,
        },
        sectionOrder: [],
        disabledSections: {},
        protectedSections: {},
        publishedFiles: [],
        headerTemplate: '# [Document Title]\n\n**Date:** [Current Date]',
        footerTemplate: 'Page [Page Number] of [Total Pages]\n\n© [Current Year] - Confidential',
        styleSettings: {
          fontFamily: 'Times New Roman',
          fontSize: '12pt',
          lineSpacing: '1.5',
          marginTop: '1in',
          marginBottom: '1in',
          marginLeft: '1in',
          marginRight: '1in',
          pageSize: 'A4',
          orientation: 'portrait'
        },
        resources: [],
        version: 1,
      },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  // Add new node creation function for NodeWebExtract
  const createNodeWebExtract = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `web-extract-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodewebextract',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'Web Extract',
        informationClassification: 'Data',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        url: '',
        mode: 'normal',
        includeImages: false,
        extractedContent: '',
        images: [],
        lastRefreshed: '',
        automationEnabled: false,
        schedule: null,
        statusMessage: '',
        version: 1,
      },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  // Add new node creation function for NodeFacilitator
  const createNodeFacilitator = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `facilitator-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodefacilitator',
      position,
      width: 320,
      height: 450,
      data: {
        label: 'AI Facilitator',
        informationClassification: 'Intelligence',
        createdBy: userEmail, createdAt: new Date().toISOString(),
        messages: [],
        currentPersonality: 'assistant', aiEngine: 'xai',
        model: 'grok-2-latest',
        contextScope: 'global',
        autonomousLevel: 'confirmation',
        groupChatEnabled: false,
        isCollaborativeSession: false,
        analysisEnabled: true,
        dikwExtractionEnabled: true,
        nodeCreationSuggestions: true, configuration: {
          systemPrompt: '',
          temperature: 0.7,
          maxTokens: 0,
          customPersonalities: []
        },
        statusMessage: '',
        version: 1,
      },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  // Add new node creation function for NodeResearcher
  const createNodeResearcher = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `researcher-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'noderesearcher',
      position,
      width: 1200,
      height: 900,
      data: {
        label: 'AI Researcher',
        informationClassification: 'Intelligence',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        messages: [],
        currentPersonality: 'researcher',
        aiEngine: 'xai',
        model: 'grok-2-latest',
        contextScope: 'global',
        autonomousLevel: 'confirmation',
        groupChatEnabled: false,
        isCollaborativeSession: false,
        analysisEnabled: true,
        dikwExtractionEnabled: true,
        nodeCreationSuggestions: true,
        configuration: {
          systemPrompt: '',
          temperature: 0.7,
          maxTokens: 0,
          customPersonalities: []
        },
        statusMessage: '',
        version: 1,
      },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createNodeDomainKnowledge = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `domain-knowledge-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodedomainknowledge',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'Domain Knowledge',
        informationClassification: 'Knowledge',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        domain: '',
        subdomain: '',
        domainKnowledge: null,
        statusMessage: '',
        isMinimized: false,
        isLocked: false,
        assignedEmails: [],
        documentRequest: undefined,
        dataSettings: {},
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  // Create HTML Fields node function
  const createNodeHTMLFields = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `html-fields-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodehtmlfields',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'HTML Fields',
        informationClassification: 'Deliverable',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        fieldSetName: '',
        htmlTemplate: '',
        fieldType: 'Input Form',
        cssClasses: '',
        fields: [],
        statusMessage: '',
        isMinimized: false,
        isLocked: false,
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  // Create Group Blueprint node function
  const createNodeGroupBlueprint = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `group-blueprint-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodegroupblueprint',
      position,
      width: 900,
      height: 930,
      data: {
        label: 'Group Blueprint',
        informationClassification: 'Synthesis',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        blueprintReference: '',
        inputMappings: '',
        outputMappings: '',
        inheritSettings: false,
        blueprintId: null,
        configuration: {},
        statusMessage: '',
        isMinimized: false,
        isLocked: false,
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  // Create Workflow node function
  const createNodeWorkflow = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `workflow-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodeworkflow',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'Workflow',
        informationClassification: 'Synthesis',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        workflowName: '',
        workflowSteps: '',
        executionMode: 'Sequential',
        timeoutMinutes: 30,
        steps: [],
        currentStep: 0,
        isRunning: false,
        statusMessage: '',
        isMinimized: false,
        isLocked: false,
        version: 1,
      },
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createNodeAIKnot = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `ai-knot-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodeaiknot',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'AI Knot',
        informationClassification: 'Knowledge',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        selectedTemplate: '',
        customOperation: '',
        llmPrompt: '',
        templateConfig: null,
        inputMode: 'loose',
        outputMode: 'loose',
        inputJsonStructure: '',
        outputJsonStructure: '',
        inputFormatPrompt: '',
        outputFormatPrompt: '',
        outputText: '',
        automationEnabled: false,
        schedule: null,
        usageCounters: {
          totalRuns: 0,
          successfulRuns: 0,
          failedRuns: 0,
          avgProcessingTimeMs: 0,
          avgInputSizeBytes: 0,
          avgOutputSizeBytes: 0,
          totalDataProcessedBytes: 0
        },
        version: 1,
      },
      draggable: true,
      selectable: true,
      connectable: true,
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  const createNodeWebSearch = useCallback(() => {
    const position = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    });
    const newNode: Node = {
      id: `web-search-node-${new Date().toISOString().replace(/[-:.TZ]/g, '')}`,
      type: 'nodewebsearch',
      position,
      width: 900,
      height: 630,
      data: {
        label: 'Web Search',
        informationClassification: 'Data',
        createdBy: userEmail,
        createdAt: new Date().toISOString(),
        query: '',
        searchPreset: 'general',
        maxResults: 10,
        searchDepth: 'basic',
        includeDomains: '',
        excludeDomains: '',
        searchResults: [],
        selectedResults: [],
        statusMessage: '',
        isMinimized: false,
        isLocked: false,
        version: 1,
      },
      draggable: true,
      selectable: true,
      resizing: true,
      connectable: true,
    };
    addNode(newNode);
  }, [screenToFlowPosition, addNode, userEmail]);

  // Higher-level function to create nodes based on type string
  const createNodeByType = useCallback(
    (nodeType: string) => {
      console.log('Creating node. Type', nodeType);
      switch (nodeType) {
        case 'NodeQuickStart':
          return createQuickStartNode();
        case 'NodeQuickStartDynamic':
          return createNodeQuickStartDynamic();
        case 'NodeDocument':
          return createDocumentNode();
        case 'NodeIntellica':
          return createNodeIntellica();
        case 'NodeSmartNotes':
          return createNodeSmartNotes();
        case 'NodeDocumentDesigner':
          return createDocumentDesignerNode();
        case 'NodePlantUML':
          return createNodeDiagrams();
        case 'NodeDiagramsMermaid':
          return createNodeDiagramsMermaid();
        case 'NodeFileMiner':
          return createFileMinerNode();
        case 'NodeJavascript':
          return createJavascriptNode();
        case 'NodeImageGenerator':
          return createImageGeneratorNode();
        case 'NodeFileResource':
          return createNodeResource({
            fileId: '',
            filename: '',
            url: '',
            contentType: '',
            size: 0,
            createdAt: new Date().toISOString(),
            groupId: null,
          });
        case 'NodeStrategy':
          return createStrategyNode();
        case 'NodePresence':
          return createPresenceNode();
        case 'NodeAgency':
          return createAgencyNode();
        case 'NodeKnowledgeSet':
          return createnodeknowledgeset();
        case 'nodequickstartdynamic':
          return createnodequickstartdynamic();
        case 'NodeComposite':
          return createNodeComposite(); case 'NodeWebExtract':
          return createNodeWebExtract(); case 'NodeFacilitator':
          return createNodeFacilitator();
        case 'NodeResearcher':
          return createNodeResearcher();
        case 'NodeGlobalConstants':
          return createGlobalConstantsNode();
        case 'NodeAgent':
          return createNodeAgent();
        case 'NodeDomainKnowledge':
          return createNodeDomainKnowledge();
        case 'NodeHTMLFields':
          return createNodeHTMLFields();
        case 'NodeGroupBlueprint':
          return createNodeGroupBlueprint();
        case 'NodeWorkflow':
          return createNodeWorkflow();
        case 'NodeAIKnot':
          return createNodeAIKnot();
        case 'NodeWebSearch':
          return createNodeWebSearch();
        default:
          console.warn(`Unknown node type: ${nodeType}`);
          return null;
      }
    },
    [
      createQuickStartNode,
      createDocumentNode,
      createNodeIntellica,
      createNodeSmartNotes,
      createDocumentDesignerNode,
      createNodeDiagrams,
      createNodeDiagramsMermaid,
      createFileMinerNode,
      createJavascriptNode,
      createImageGeneratorNode,
      createNodeResource,
      createStrategyNode,
      createPresenceNode,
      createAgencyNode,
      createnodeknowledgeset,
      createnodequickstartdynamic, createNodeComposite, createNodeWebExtract,
      createNodeFacilitator,
      createNodeResearcher,
      createGlobalConstantsNode,
      createNodeAgent,
      createNodeDomainKnowledge,
      createNodeHTMLFields,
      createNodeGroupBlueprint,
      createNodeWorkflow,
      createNodeAIKnot,
      createNodeWebSearch,
    ]
  );

  // Return all the creation functions
  return {
    createQuickStartNode,
    createnodequickstartdynamic,
    createDocumentNode,
    createDocumentNodeWithContent: createDocumentNodeWithContent, // Expose the new function
    createNodeIntellica,
    createSmartNotesNode: createNodeSmartNotes,
    createDocumentDesignerNode,
    createNodeDiagrams,
    createNodeDiagramsMermaid,
    createFileMinerNode,
    createJavascriptNode,
    createImageGeneratorNode,
    createNodeResource,
    createStrategyNode,
    createPresenceNode,
    createAgencyNode,
    createnodeknowledgeset, createNodeComposite, createNodeWebExtract, // Add this line
    createNodeFacilitator,
    createNodeResearcher,
    createGlobalConstantsNode,
    createNodeAgent,
    createNodeDomainKnowledge,
    createNodeHTMLFields,
    createNodeGroupBlueprint,
    createNodeWorkflow,
    createNodeAIKnot,
    createNodeWebSearch,
    createNodeByType,
  };
}